import { useState } from "react";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import api from "@/lib/api";
import { Newspaper, Sparkles, Check, X, Loader2, ArrowRight } from "lucide-react";

/**
 * Admin — News Ingest.
 *
 * Paste an article, Facebook post, tweet, or press release. Claude parses
 * every coaching move it can find, admin reviews the checklist, one click
 * commits the approved subset. Powers the "watch this page for updates"
 * workflow without needing a scraper for every source.
 */
export default function AdminNewsIngest() {
  const [text, setText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [parsed, setParsed] = useState(null);
  const [selected, setSelected] = useState({});
  const [busyParse, setBusyParse] = useState(false);
  const [busyApply, setBusyApply] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const parse = async () => {
    if (text.trim().length < 30) {
      toast.warning("Paste at least a paragraph of text");
      return;
    }
    setBusyParse(true);
    setParsed(null);
    setSelected({});
    try {
      const { data } = await api.post("/admin/news-ingest/parse", {
        text,
        source_url: sourceUrl || null,
      });
      setParsed(data);
      // Pre-select all moves by default — admin can uncheck what looks wrong.
      const initial = {};
      (data.moves || []).forEach((_, i) => { initial[i] = true; });
      setSelected(initial);
      if ((data.moves || []).length === 0) {
        toast.info(`No coaching moves detected. Summary: ${data.summary || "—"}`);
      } else {
        toast.success(`Found ${(data.moves || []).length} move(s). Review + apply below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parse failed");
    } finally {
      setBusyParse(false);
    }
  };

  const apply = async () => {
    if (!parsed) return;
    const moves = (parsed.moves || []).filter((_, i) => selected[i]);
    if (moves.length === 0) {
      toast.warning("Select at least one move to apply");
      return;
    }
    setBusyApply(true);
    try {
      const { data } = await api.post("/admin/news-ingest/apply", {
        moves,
        source_url: parsed.source_url || null,
      });
      setLastRun(data);
      toast.success(
        `Applied. ${data.coaches_updated} updated, ${data.coaches_created} created, ${data.leads_closed} leads closed.`
      );
      // Clear state for next paste
      setText("");
      setSourceUrl("");
      setParsed(null);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setBusyApply(false);
    }
  };

  return (
    <>
      <main className="text-white pb-24">
        <section className="px-4 md:px-8 lg:px-12 py-10 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Newspaper className="text-blaze" size={28} strokeWidth={1.75} />
            <h1 className="heading text-3xl md:text-4xl">NEWS INGEST</h1>
          </div>
          <p className="text-sm text-neutral-400 max-w-2xl">
            Paste an article, Facebook / X post, press release, or listserv email
            below. Claude will extract every coaching move it can find. You review
            the checklist. One click commits — coach profiles are stubbed / updated
            and matching open outreach leads are closed.
          </p>

          {/* Input */}
          <div className="mt-8 border border-neutral-800 bg-appbg p-5">
            <label className="text-[11px] tracking-widest text-neutral-500 block mb-1">SOURCE URL (OPTIONAL)</label>
            <input
              type="text"
              data-testid="news-ingest-url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://facebook.com/… or https://insidelacrosse.com/…"
              className="input mb-4"
            />
            <label className="text-[11px] tracking-widest text-neutral-500 block mb-1">PASTE ARTICLE / POST TEXT</label>
            <textarea
              data-testid="news-ingest-text"
              className="input min-h-[220px] text-sm leading-relaxed"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. 'According to reports, Dave Metzbower is set to be new Fairfield coach John Odierna's OC…'"
            />
            <div className="mt-4 flex items-center gap-3">
              <button
                data-testid="news-ingest-parse-btn"
                onClick={parse}
                disabled={busyParse}
                className="h-11 px-5 bg-blaze text-white heading text-sm tracking-widest inline-flex items-center gap-2 hover:glow-blaze disabled:opacity-50"
              >
                {busyParse ? <><Loader2 className="animate-spin" size={16} /> PARSING…</> : <><Sparkles size={16} /> PARSE WITH AI</>}
              </button>
              {text && (
                <span className="text-xs text-neutral-500">{text.length.toLocaleString()} chars · Claude has an 8,000-char window</span>
              )}
            </div>
          </div>

          {/* Preview */}
          {parsed && (
            <div className="mt-8 border border-volt/40 bg-[#0A101A] p-5" data-testid="news-ingest-preview">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] tracking-widest text-volt">CLAUDE&apos;S READ</div>
              </div>
              <p className="text-sm text-neutral-300 italic mb-4">{parsed.summary || "(no summary returned)"}</p>

              {(parsed.moves || []).length === 0 ? (
                <div className="text-sm text-neutral-500">No coaching moves detected.</div>
              ) : (
                <div className="space-y-3">
                  <div className="text-[10px] tracking-widest text-blaze">STAFF MOVES · {parsed.moves.length}</div>
                  {parsed.moves.map((m, i) => (
                    <label
                      key={i}
                      className={`block border p-4 cursor-pointer transition-colors ${selected[i] ? "border-volt bg-volt/5" : "border-neutral-800 hover:border-neutral-600"}`}
                      data-testid={`news-move-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          data-testid={`news-move-check-${i}`}
                          checked={!!selected[i]}
                          onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                          className="mt-1 w-4 h-4 accent-volt shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="heading text-lg text-white">{m.coach_name}</span>
                            {m.sport && <span className="text-[10px] tracking-widest text-neutral-500">{m.sport.toUpperCase()}</span>}
                            {m.gender && <span className="text-[10px] tracking-widest text-neutral-500">· {m.gender.toUpperCase()}</span>}
                            {m.level && <span className="text-[10px] tracking-widest text-neutral-500">· {m.level.toUpperCase()}</span>}
                            {m.division && <span className="text-[10px] tracking-widest text-neutral-500">· {m.division}</span>}
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-sm text-neutral-300 flex-wrap">
                            <span className="text-neutral-500">{m.from_org ? `${m.from_role || "Coach"} @ ${m.from_org}` : "—"}</span>
                            <ArrowRight size={14} className="text-blaze shrink-0" />
                            <span className="text-white font-medium">{m.to_role} @ {m.to_org}</span>
                            {m.effective_date && <span className="text-[10px] tracking-widest text-neutral-500 border border-neutral-700 px-1.5 py-0.5">{m.effective_date}</span>}
                          </div>
                          {m.source_snippet && (
                            <div className="mt-2 text-xs text-neutral-500 italic border-l-2 border-neutral-700 pl-3">&ldquo;{m.source_snippet}&rdquo;</div>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {(parsed.openings || []).length > 0 && (
                <div className="mt-6 pt-4 border-t border-neutral-800">
                  <div className="text-[10px] tracking-widest text-blaze mb-2">SIGNALED OPENINGS · {parsed.openings.length}</div>
                  <ul className="text-sm text-neutral-300 space-y-1">
                    {parsed.openings.map((o, i) => (
                      <li key={i} className="border border-neutral-800 px-3 py-2">
                        <b className="text-white">{o.role}</b> @ {o.program_name}
                        {o.gender && <span className="text-neutral-500 text-xs"> · {o.gender}</span>}
                        {o.level && <span className="text-neutral-500 text-xs"> · {o.level}</span>}
                        {o.reason && <div className="text-xs text-neutral-500 mt-0.5">{o.reason}</div>}
                      </li>
                    ))}
                  </ul>
                  <div className="text-[10px] text-neutral-500 mt-2">Openings are informational only — no automated write for these yet.</div>
                </div>
              )}

              {(parsed.moves || []).length > 0 && (
                <div className="mt-6 flex items-center gap-3">
                  <button
                    data-testid="news-ingest-apply-btn"
                    onClick={apply}
                    disabled={busyApply}
                    className="h-11 px-5 bg-volt text-white heading text-sm tracking-widest inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
                  >
                    {busyApply ? <><Loader2 className="animate-spin" size={16} /> APPLYING…</> : <><Check size={16} /> APPLY SELECTED ({Object.values(selected).filter(Boolean).length})</>}
                  </button>
                  <button
                    onClick={() => { setParsed(null); setSelected({}); }}
                    className="h-11 px-4 border border-neutral-700 text-neutral-400 text-xs tracking-widest inline-flex items-center gap-2 hover:border-neutral-500"
                    data-testid="news-ingest-discard-btn"
                  >
                    <X size={14} /> DISCARD
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Last-run receipt */}
          {lastRun && (
            <div className="mt-6 border border-blaze/40 bg-[#0A0505] p-4" data-testid="news-ingest-last-run">
              <div className="text-[10px] tracking-widest text-blaze mb-2">LAST APPLIED</div>
              <div className="text-sm text-neutral-300">
                {lastRun.coaches_updated} updated · {lastRun.coaches_created} created · {lastRun.leads_closed} leads closed
              </div>
              <ul className="mt-2 text-xs text-neutral-400 space-y-1">
                {(lastRun.log || []).map((l, i) => <li key={i}>· {l.action}: {l.name || l.program} → {l.to || `x${l.count}`}</li>)}
              </ul>
            </div>
          )}
        </section>
        <Footer />
      </main>
    </>
  );
}
